/**
 * SettlementInstructionsAdminPanel — "Settlement Instructions" admin tab
 * Composes RuleListView + rule editor + SettlementInstructionActionEditor.
 * Sets context = "settlement_instructions" automatically.
 * Cloned from ContractsAdminPanel — same structure, different action editor.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { rulesEngineService } from '../../services/api/rulesEngineService';
import { bankService } from '../../services/api/bankService';
import type { Bank } from '../../services/api/bankService';
import { clientService } from '../../services/api/clientService';
import type { TradingEntity } from '../../services/api/clientService';
import { groupingsService } from '../../services/api/groupingsService';
import type { GroupingsConfig } from '../../types/groupings';
import RuleListView from '../rules/RuleListView';
import RuleConditionBuilder from '../rules/RuleConditionBuilder';
import SettlementInstructionActionEditor from './SettlementInstructionActionEditor';
import AlertModal from '../common/AlertModal';
import type { Rule, RuleCreate, RuleUpdate, ConditionGroup, ConflictResult, FieldCatalogueEntry } from '../../types/rulesEngine';
import './ContractsAdminPanel.css';

const CONTEXT = 'settlement_instructions';

type ViewMode = 'list' | 'create' | 'edit';

const DEFAULT_ACTION: Record<string, unknown> = {
  templateId: '',
  templateName: '',
  outputFormat: 'DOCX',
  cargarAccountId: '',
  cargarAccountName: '',
  abonarAccountId: '',
  abonarAccountName: '',
  centralBankTradeCode: '',
};

const SettlementInstructionsAdminPanel: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Editor form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [conditionGroups, setConditionGroups] = useState<ConditionGroup[]>([]);
  const [action, setAction] = useState<Record<string, unknown>>(DEFAULT_ACTION);

  // Dynamic field catalogue data
  const [banks, setBanks] = useState<Bank[]>([]);
  const [tradingEntities, setTradingEntities] = useState<TradingEntity[]>([]);
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<ConflictResult[]>([]);
  const [alertMessage, setAlertMessage] = useState('');

  // Load dynamic data for field catalogue
  useEffect(() => {
    if (!clientId) return;
    const load = async () => {
      const [banksRes, entitiesRes, groupings] = await Promise.all([
        bankService.listBanks().catch(() => null),
        clientService.getTradingEntities(clientId).catch(() => null),
        groupingsService.getGroupings(clientId).catch(() => null),
      ]);
      if (banksRes?.success && banksRes.data) setBanks(banksRes.data);
      if (entitiesRes?.success && entitiesRes.data) setTradingEntities(entitiesRes.data);
      if (groupings) setGroupingsConfig(groupings);
    };
    load();
  }, [clientId]);

  // Build field catalogue (identical to contract_signing — same trade conditions)
  const fieldCatalogue = useMemo<FieldCatalogueEntry[]>(() => {
    const NUMBER_OPERATORS: FieldCatalogueEntry['operators'] = [
      'equals', 'not_equals', 'greater_than', 'less_than',
      'greater_than_or_equal', 'less_than_or_equal', 'between',
    ];
    const DATE_OPERATORS = NUMBER_OPERATORS;

    const catalogue: FieldCatalogueEntry[] = [
      {
        displayName: 'Product Type',
        field: 'ProductType',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: [
          { label: 'Spot', value: 'Spot' },
          { label: 'Forward', value: 'Forward' },
          { label: 'Forward Asiático', value: 'Forward Asiático' },
          { label: 'Forward Starting', value: 'Forward Starting' },
        ],
      },
      {
        displayName: 'Settlement Type',
        field: 'SettlementType',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: [
          { label: 'Entrega Física', value: 'Entrega Física' },
          { label: 'Compensación', value: 'Compensación' },
        ],
      },
      {
        displayName: 'Settlement Currency',
        field: 'SettlementCurrency',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'text',
      },
      {
        displayName: 'Direction',
        field: 'Direction',
        valueType: 'string',
        operators: ['equals', 'not_equals'],
        inputType: 'dropdown',
        options: [
          { label: 'Buy', value: 'Buy' },
          { label: 'Sell', value: 'Sell' },
        ],
      },
      {
        displayName: 'Our Party',
        field: 'OurParty',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: tradingEntities.map(e => ({ label: e.name, value: e.name })),
      },
      {
        displayName: 'Counterparty',
        field: 'CounterpartyName',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'dropdown',
        options: banks.filter(b => b.isCounterparty !== false).map(b => ({ label: b.name, value: b.name })),
      },
      {
        displayName: 'Currency 1',
        field: 'Currency1',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'text',
      },
      {
        displayName: 'Currency 2',
        field: 'Currency2',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in'],
        inputType: 'text',
      },
      {
        displayName: 'Notional Amount',
        field: 'QuantityCurrency1',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: 'Contra Amount',
        field: 'QuantityCurrency2',
        valueType: 'number',
        operators: NUMBER_OPERATORS,
        inputType: 'number',
      },
      {
        displayName: 'Trade Date',
        field: 'TradeDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: 'Value Date',
        field: 'ValueDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: 'Maturity Date',
        field: 'MaturityDate',
        valueType: 'date',
        operators: DATE_OPERATORS,
        inputType: 'datepicker',
      },
      {
        displayName: 'Parent Trade Number',
        field: 'internalTradeRef.parentTradeNumber',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: 'text',
      },
      {
        displayName: 'Internal Trade',
        field: 'IsInternalTrade',
        valueType: 'string',
        operators: ['equals', 'not_equals'],
        inputType: 'dropdown',
        options: [
          { label: 'true', value: 'true' },
          { label: 'false', value: 'false' },
        ],
      },
    ];

    if (groupingsConfig?.grouping1Label) {
      const activeValues = groupingsConfig.grouping1Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping1Label,
        field: 'Grouping1',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    if (groupingsConfig?.grouping2Label) {
      const activeValues = groupingsConfig.grouping2Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping2Label,
        field: 'Grouping2',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    if (groupingsConfig?.grouping3Label) {
      const activeValues = groupingsConfig.grouping3Values.filter(v => v.isActive);
      catalogue.push({
        displayName: groupingsConfig.grouping3Label,
        field: 'Grouping3',
        valueType: 'string',
        operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
        inputType: activeValues.length > 0 ? 'dropdown' : 'text',
        options: activeValues.map(v => ({ label: v.name, value: v.name })),
      });
    }

    return catalogue;
  }, [banks, tradingEntities, groupingsConfig]);

  // ── Table summary functions ──

  const OPERATOR_LABELS: Record<string, string> = {
    equals: '=', not_equals: '≠',
    in: '∈', not_in: '∉',
    greater_than: '>', less_than: '<',
    greater_than_or_equal: '≥', less_than_or_equal: '≤',
    between: '↔', contains: '⊃', not_contains: '⊅',
    is_empty: t('rules.operatorIsEmpty', 'is empty'),
    is_not_empty: t('rules.operatorIsNotEmpty', 'is not empty'),
  };

  const conditionSummaryFn = useCallback((rule: Rule): string => {
    if (!rule.conditionGroups || rule.conditionGroups.length === 0) {
      return t('rules.defaultRule', 'Applies to all trades');
    }
    const groupTexts = rule.conditionGroups.map(group => {
      const parts = group.conditions.map(cond => {
        const catalogueEntry = fieldCatalogue.find(f => f.field === cond.field);
        const fieldLabel = catalogueEntry?.displayName || cond.field;
        const op = OPERATOR_LABELS[cond.operator] || cond.operator;

        if (cond.operator === 'is_empty' || cond.operator === 'is_not_empty') {
          return `${fieldLabel} ${op}`;
        }

        let valueStr = '';
        if (Array.isArray(cond.value)) {
          valueStr = cond.value.join(', ');
        } else if (cond.value != null && typeof cond.value === 'object') {
          const obj = cond.value as Record<string, unknown>;
          valueStr = `${obj.from ?? ''}–${obj.to ?? ''}`;
        } else {
          valueStr = String(cond.value ?? '');
        }

        return `${fieldLabel} ${op} ${valueStr}`;
      });
      return parts.join(' & ');
    });
    return groupTexts.join(' | ');
  }, [fieldCatalogue, t]);

  const actionSummaryFn = useCallback((rule: Rule): string => {
    const a = rule.action as Record<string, unknown>;
    const tplId = a.templateId as string;

    // No template selected → N/A
    if (!tplId) return 'N/A';

    const parts: string[] = [];

    const tplName = (a.templateName as string) || tplId;
    parts.push(tplName);

    const fmt = (a.outputFormat as string) || 'DOCX';
    parts.push(fmt);

    const code = a.centralBankTradeCode as string;
    if (code) parts.push(code);

    return parts.join(' · ');
  }, []);

  const timingSummaryFn = useCallback((rule: Rule): string => {
    const a = rule.action as Record<string, unknown>;

    // No template selected → timing is irrelevant
    if (!(a.templateId as string)) return 'N/A';

    const timing = (a.timing as string[]) || ['onConfirmation'];
    const labels: Record<string, string> = {
      onTrade: t('admin.settlementInstructions.timingOnTrade', 'Al ingresar operación'),
      onConfirmation: t('admin.settlementInstructions.timingOnConfirmation', 'Al confirmar operación'),
      onExpiry: t('admin.settlementInstructions.timingOnExpiry', 'Al vencimiento / unwind'),
      onExpiryConfirmation: t('admin.settlementInstructions.timingOnExpiryConfirmation', 'Al confirmar vencimiento / unwind'),
    };
    return timing.map(v => labels[v] || v).join(', ');
  }, [t]);

  const pagamosSummaryFn = useCallback((rule: Rule): string => {
    const a = rule.action as Record<string, unknown>;
    const account = (a.cargarAccountName as string) || '';
    return account || '—';
  }, []);

  const recibimosSummaryFn = useCallback((rule: Rule): string => {
    const a = rule.action as Record<string, unknown>;
    const account = (a.abonarAccountName as string) || '';
    return account || '—';
  }, []);

  const extraColumns = useMemo(() => [
    { key: 'pagamos', label: t('admin.settlementInstructions.pagamosColumn', 'Pagamos'), fn: pagamosSummaryFn },
    { key: 'recibimos', label: t('admin.settlementInstructions.recibimosColumn', 'Recibimos'), fn: recibimosSummaryFn },
  ], [t, pagamosSummaryFn, recibimosSummaryFn]);

  // Reset form
  const resetForm = () => {
    setName('');
    setDescription('');
    setIsActive(true);
    setConditionGroups([]);
    setAction({ ...DEFAULT_ACTION });
    setConflicts([]);
  };

  const handleCreate = () => {
    resetForm();
    setEditingRule(null);
    setViewMode('create');
  };

  const handleEdit = (rule: Rule) => {
    setEditingRule(rule);
    setName(rule.name);
    setDescription(rule.description || '');
    setIsActive(rule.isActive);
    setConditionGroups(rule.conditionGroups);
    setAction(rule.action);
    setConflicts([]);
    setViewMode('edit');
  };

  const handleCopy = async (rule: Rule) => {
    try {
      const payload: RuleCreate = {
        name: `${rule.name} (${t('rules.copyNoun', 'Copia')})`,
        description: rule.description || undefined,
        context: CONTEXT,
        isActive: rule.isActive,
        conditionGroups: rule.conditionGroups,
        action: rule.action,
      };
      const response = await rulesEngineService.createRule(clientId, payload);
      if (response.success && response.data) {
        setConflicts(response.data.conflicts || []);
        setRefreshKey(k => k + 1);
      }
    } catch (err: any) {
      setAlertMessage(err.message || t('common.error', 'An error occurred'));
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setEditingRule(null);
    setConflicts([]);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setAlertMessage(t('rules.nameRequired', 'Rule name is required.'));
      return;
    }

    setSaving(true);
    try {
      if (viewMode === 'create') {
        const payload: RuleCreate = {
          name: name.trim(),
          description: description.trim() || undefined,
          context: CONTEXT,
          isActive,
          conditionGroups,
          action,
        };
        const response = await rulesEngineService.createRule(clientId, payload);
        if (response.success && response.data) {
          setConflicts(response.data.conflicts || []);
          setRefreshKey(k => k + 1);
          setViewMode('list');
        }
      } else if (viewMode === 'edit' && editingRule) {
        const payload: RuleUpdate = {
          name: name.trim(),
          description: description.trim() || undefined,
          isActive,
          conditionGroups,
          action,
        };
        const response = await rulesEngineService.updateRule(clientId, editingRule.id, payload);
        if (response.success && response.data) {
          setConflicts(response.data.conflicts || []);
          setRefreshKey(k => k + 1);
          setViewMode('list');
        }
      }
    } catch (err: any) {
      setAlertMessage(err.message || t('common.error', 'An error occurred'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──

  if (viewMode === 'list') {
    return (
      <div className="contracts-admin-panel">
        {conflicts.length > 0 && (
          <div className="conflict-warning-banner">
            <div className="conflict-warning-title">
              {t('rules.conflictWarning', 'Warning: Rule overlap detected')}
            </div>
            {conflicts.map((c, i) => (
              <div key={i} className="conflict-item">
                "{c.conflictingRuleName}": {c.overlapDescription}
              </div>
            ))}
            <div className="conflict-dismiss-hint">
              {t('rules.conflictDismissHint', 'This is not an error. Multiple matching rules are allowed. Review your rules to ensure the overlap is intentional.')}
            </div>
            <button className="dismiss-btn" onClick={() => setConflicts([])}>
              {t('common.dismiss', 'Dismiss')}
            </button>
          </div>
        )}

        <RuleListView
          context={CONTEXT}
          title={t('admin.settlementInstructions.title', 'Reglas de Cartas de Instrucción')}
          onEdit={handleEdit}
          onCreate={handleCreate}
          onCopy={handleCopy}
          refreshKey={refreshKey}
          conditionSummaryFn={conditionSummaryFn}
          actionSummaryFn={actionSummaryFn}
          actionSummaryLabel={t('admin.settlementInstructions.documentColumn', 'Carta de Instrucción')}
          timingSummaryFn={timingSummaryFn}
          extraColumns={extraColumns}
        />

        {alertMessage && (
          <AlertModal message={alertMessage} onClose={() => setAlertMessage('')} />
        )}
      </div>
    );
  }

  const isDefault = viewMode === 'edit' && editingRule && editingRule.conditionGroups.length === 0;

  return (
    <div className="contracts-admin-panel">
      <div className="editor-header">
        <h3>
          {viewMode === 'create'
            ? t('rules.addRule', 'Add Rule')
            : t('rules.editRule', 'Edit Rule')}
        </h3>
      </div>

      <div className="editor-form">
        <div className="form-row-inline">
          <div className="form-field form-field-name">
            <label>{t('rules.name', 'Name')}</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('rules.namePlaceholder', 'e.g. BCI — Spot, DOCX')}
              maxLength={200}
            />
          </div>

          <div className="form-field form-field-desc">
            <label>{t('rules.description', 'Description')} <span className="optional">({t('common.optional', 'optional')})</span></label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('rules.descriptionPlaceholder', 'Brief description')}
              maxLength={500}
            />
          </div>

          {!isDefault && (
            <div className="form-field form-field-active">
              <label className="active-checkbox-label">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                />
                {t('rules.isActive', 'Active')}
              </label>
            </div>
          )}
        </div>

        <div className="if-then-layout">
          <div className="if-section">
            <div className="section-label">{t('rules.ifLabel', 'If')}</div>
            <RuleConditionBuilder
              conditionGroups={conditionGroups}
              fieldCatalogue={fieldCatalogue}
              onChange={isDefault ? () => {} : setConditionGroups}
            />
          </div>
          <div className="then-arrow">&rarr;</div>
          <div className="then-section">
            <div className="section-label">{t('rules.thenLabel', 'Then')}</div>
            <SettlementInstructionActionEditor
              action={action}
              onChange={setAction}
              conditionGroups={conditionGroups}
            />
          </div>
        </div>

        <div className="editor-actions">
          <button className="cancel-btn" onClick={handleBack} disabled={saving}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </div>
      </div>

      {alertMessage && (
        <AlertModal message={alertMessage} onClose={() => setAlertMessage('')} />
      )}
    </div>
  );
};

export default SettlementInstructionsAdminPanel;
